import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Upload, FileText, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { bidService } from '../services/bidService';

interface BidUploadComponentProps {
  isOpen: boolean;
  onClose: () => void;
  onBidUploaded: (bidId: string) => void;
}

type UploadStage = 'select' | 'uploading' | 'processing' | 'complete' | 'error';

export const BidUploadComponent: React.FC<BidUploadComponentProps> = ({
  isOpen,
  onClose,
  onBidUploaded,
}) => {
  const [stage, setStage] = useState<UploadStage>('select');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string>('');
  const [bidId, setBidId] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetForm = () => {
    setStage('select');
    setSelectedFile(null);
    setError('');
    setBidId('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setError('Please select a PDF file');
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
      setError('');
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const file = event.dataTransfer.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setError('Please select a PDF file');
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
      setError('');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setStage('uploading');
      setError('');

      const result = await bidService.uploadBid(selectedFile);
      setBidId(result.bid.id);

      // Immediately redirect to detail page where polling will happen
      onBidUploaded(result.bid.id);
      handleClose();
    } catch (err) {
      console.error('Error uploading bid:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload bid');
      setStage('error');
    }
  };

  const renderStage = () => {
    switch (stage) {
      case 'select':
        return (
          <div className="space-y-4">
            <div
              className={`relative border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all ${
                selectedFile
                  ? 'border-green-400 bg-green-50/50'
                  : 'border-gray-300 hover:border-fxer-blue hover:bg-gray-50'
              }`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                className="hidden"
              />

              {selectedFile ? (
                <div className="flex flex-col items-center gap-3">
                  <div className="relative">
                    <div className="absolute inset-0 bg-green-400 rounded-full animate-ping opacity-20"></div>
                    <CheckCircle className="relative h-16 w-16 text-green-600" />
                  </div>
                  <div className="max-w-full px-4">
                    <p className="text-base font-semibold text-gray-900 truncate">
                      {selectedFile.name}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB • PDF Document
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    className="mt-2"
                  >
                    Choose Different File
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-4">
                  <div className="relative">
                    <div className="absolute inset-0 bg-fxer-blue rounded-full opacity-10"></div>
                    <Upload className="relative h-16 w-16 text-fxer-blue" />
                  </div>
                  <div>
                    <p className="text-base font-semibold text-gray-900">
                      Drop your PDF here
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      or click to browse files
                    </p>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full">
                    <FileText className="h-4 w-4 text-gray-600" />
                    <span className="text-xs font-medium text-gray-600">PDF files only</span>
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-900">Upload Error</p>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
              </div>
            )}

            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 bg-blue-600 rounded-full"></span>
                What happens next?
              </h4>
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-blue-800">Secure upload to cloud storage</p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-blue-800">AI extracts contractor info, line items, and pricing</p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-blue-800">Real-time processing status updates</p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-blue-800">View detailed analysis and comparisons</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'uploading':
        return (
          <div className="py-12 text-center">
            <div className="flex flex-col items-center space-y-6">
              <div className="relative">
                <div className="absolute inset-0 bg-fxer-blue rounded-full animate-ping opacity-20"></div>
                <Loader2 className="relative h-16 w-16 text-fxer-blue animate-spin" />
              </div>
              <div>
                <p className="text-xl font-semibold text-gray-900">Uploading bid...</p>
                <p className="text-sm text-gray-500 mt-2">Preparing your document for analysis</p>
              </div>
            </div>
          </div>
        );

      case 'processing':
        return (
          <div className="py-12 text-center">
            <div className="flex flex-col items-center space-y-6">
              <div className="relative">
                <div className="absolute inset-0 bg-fxer-blue rounded-full animate-ping opacity-20"></div>
                <Loader2 className="relative h-16 w-16 text-fxer-blue animate-spin" />
              </div>
              <div>
                <p className="text-xl font-semibold text-gray-900">Extracting bid data...</p>
                <p className="text-sm text-gray-500 mt-2">
                  This usually takes 30-60 seconds
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 max-w-xs mx-auto">
                <div className="space-y-2 text-left">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 bg-fxer-blue rounded-full animate-pulse"></div>
                    Analyzing document structure
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 bg-fxer-blue rounded-full animate-pulse delay-100"></div>
                    Extracting contractor information
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 bg-fxer-blue rounded-full animate-pulse delay-200"></div>
                    Parsing line items and pricing
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 bg-fxer-blue rounded-full animate-pulse delay-300"></div>
                    Identifying payment terms
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'complete':
        return (
          <div className="py-12 text-center">
            <div className="flex flex-col items-center space-y-6">
              <div className="relative">
                <div className="absolute inset-0 bg-green-400 rounded-full animate-ping opacity-20"></div>
                <CheckCircle className="relative h-16 w-16 text-green-600" />
              </div>
              <div>
                <p className="text-xl font-semibold text-green-900">
                  Extraction complete!
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Redirecting to bid details...
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-600 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-green-600 rounded-full animate-bounce delay-100"></div>
                <div className="w-2 h-2 bg-green-600 rounded-full animate-bounce delay-200"></div>
              </div>
            </div>
          </div>
        );

      case 'error':
        return (
          <div className="py-12 text-center">
            <div className="flex flex-col items-center space-y-6">
              <div className="relative">
                <AlertCircle className="h-16 w-16 text-red-600" />
              </div>
              <div>
                <p className="text-xl font-semibold text-red-900">Upload failed</p>
                <p className="text-sm text-gray-600 mt-2 max-w-md">{error}</p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const getTitle = () => {
    switch (stage) {
      case 'select':
        return 'Upload Contractor Bid';
      case 'uploading':
        return 'Uploading...';
      case 'processing':
        return 'Processing...';
      case 'complete':
        return 'Complete';
      case 'error':
        return 'Error';
      default:
        return 'Upload Bid';
    }
  };

  const showActions = stage === 'select' || stage === 'error';

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">{getTitle()}</DialogTitle>
        </DialogHeader>

        <div className="overflow-hidden">
          {renderStage()}
        </div>

        {showActions && (
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>

            {stage === 'select' && (
              <Button
                onClick={handleUpload}
                disabled={!selectedFile}
                className="bg-fxer-blue hover:opacity-90"
              >
                <Upload className="mr-2 h-4 w-4" />
                Upload & Extract
              </Button>
            )}

            {stage === 'error' && (
              <Button
                onClick={() => {
                  resetForm();
                }}
                className="bg-fxer-blue hover:opacity-90"
              >
                Try Again
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
